import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useAuth } from "./auth/AuthProvider";
import ProtectedRoute from "./auth/ProtectedRoute";
import MainLayout from "./components/MainLayout";
import NavigationController from "./components/NavigationController";
import Auth from "./pages/Auth";
import Cabinet from "./pages/Cabinet/Cabinet";
import Devices from "./pages/Devices/Devices";
import History from "./pages/History";
import Instruction from "./pages/Instruction/Instruction";
import Menu from "./pages/Menu/Menu";
import Payment from "./pages/Payment";
import PaymentSuccess from "./pages/PaymentSuccess";
import Paytype from "./pages/Paytype";
import Tariffs from "./pages/Tariffs/Tariffs";
import TariffsRecalc from "./pages/TariffsRecalc";
import { storeReferralFromUrl } from "./referralStorage";
import { callTelegramWebAppMethod } from "./telegramWebApp";

function App() {
    const { isAuthorized, isReady, isTelegram } = useAuth();
    const location = useLocation();
    const [installPromptEvent, setInstallPromptEvent] = React.useState(null);
    const [isStandalone, setIsStandalone] = React.useState(false);

    React.useEffect(() => {
        storeReferralFromUrl();
    }, []);

    React.useEffect(() => {
        if (!isTelegram) {
            return undefined;
        }

        const requestFullscreen = () => callTelegramWebAppMethod("requestFullscreen");
        const expand = () => callTelegramWebAppMethod("expand");
        const expandViewport = () => {
            requestFullscreen();
            expand();
        };

        callTelegramWebAppMethod("ready");
        expandViewport();

        const frameId = window.requestAnimationFrame(expandViewport);
        const timeoutId = window.setTimeout(expandViewport, 350);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.clearTimeout(timeoutId);
        };
    }, [isTelegram]);

    React.useEffect(() => {
        const checkStandalone = () => {
            const standaloneMedia = window.matchMedia?.("(display-mode: standalone)")?.matches;
            const standaloneNavigator = window.navigator.standalone === true;
            setIsStandalone(Boolean(standaloneMedia || standaloneNavigator));
        };

        const handleBeforeInstallPrompt = (event) => {
            event.preventDefault();
            setInstallPromptEvent(event);
        };

        const handleAppInstalled = () => {
            setInstallPromptEvent(null);
            setIsStandalone(true);
        };

        checkStandalone();
        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        window.addEventListener("appinstalled", handleAppInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
            window.removeEventListener("appinstalled", handleAppInstalled);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!installPromptEvent) {
            return;
        }

        installPromptEvent.prompt();
        await installPromptEvent.userChoice;
        setInstallPromptEvent(null);
    };

    if (!isReady) {
        return <div className="flex-center h100">{isTelegram ? "Вход через Telegram..." : "Загрузка..."}</div>;
    }

    return (
        <>
            <NavigationController />
            <Routes>
                <Route element={<MainLayout />}>
                    <Route
                        path="/"
                        element={
                            isAuthorized ? (
                                <Menu
                                    shouldShowInstallButton={!isStandalone && Boolean(installPromptEvent)}
                                    onInstallClick={handleInstallClick}
                                />
                            ) : (
                                <Navigate to={{ pathname: "/auth", search: location.search }} replace />
                            )
                        }
                    />
                    <Route
                        path="/cabinet"
                        element={(
                            <ProtectedRoute>
                                <Cabinet />
                            </ProtectedRoute>
                        )}
                    />
                    <Route
                        path="/tariffs"
                        element={(
                            <ProtectedRoute>
                                <Tariffs />
                            </ProtectedRoute>
                        )}
                    />
                    <Route
                        path="/devices"
                        element={(
                            <ProtectedRoute>
                                <Devices />
                            </ProtectedRoute>
                        )}
                    />
                    <Route
                        path="/instruction"
                        element={(
                            <ProtectedRoute>
                                <Instruction />
                            </ProtectedRoute>
                        )}
                    />
                    <Route
                        path="/instruction/shared/:token"
                        element={<Instruction />}
                    />
                    <Route
                        path="/paytype"
                        element={(
                            <ProtectedRoute>
                                <Paytype />
                            </ProtectedRoute>
                        )}
                    />
                    <Route
                        path="/recalc"
                        element={(
                            <ProtectedRoute>
                                <TariffsRecalc />
                            </ProtectedRoute>
                        )}
                    />
                    <Route
                        path="/payment"
                        element={(
                            <ProtectedRoute>
                                <Payment />
                            </ProtectedRoute>
                        )}
                    />
                    <Route
                        path="/paymentsuccess"
                        element={(
                            <ProtectedRoute>
                                <PaymentSuccess />
                            </ProtectedRoute>
                        )}
                    />
                    <Route
                        path="/history"
                        element={(
                            <ProtectedRoute>
                                <History />
                            </ProtectedRoute>
                        )}
                    />
                    <Route
                        path="/cabinet/link-email"
                        element={(
                            <ProtectedRoute>
                                <Auth mode="link" />
                            </ProtectedRoute>
                        )}
                    />
                    <Route path="/auth" element={isAuthorized ? <Navigate to="/" replace /> : <Auth />} />
                    <Route
                        path="*"
                        element={(
                            <Navigate
                                to={isAuthorized ? "/" : { pathname: "/auth", search: location.search }}
                                replace
                            />
                        )}
                    />
                </Route>
            </Routes>
        </>
    );
}

export default App;
